// MACHINERY V2 (Stroje) — the machine summary card shared by SR detail and SR
// create (M2.6).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.6. Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `SrMachineCard` (lines 239-292).
// Artboards 0700 / 0701 (detail, left column 260px) and 0702 / 0703 (create,
// left column 280px — `machineSide="left"`, the designer's explicit change from
// v1, where the machine block sits under the form).
//
// ONE CARD, TWO PAGES. The detail and create screens draw the same card in the
// handoff, so it is one component here too; the only difference is the column
// width the parent grid gives it.
//
// `SrMachineCardSkeleton` (below) IS PART OF THE SAME CONTRACT. The detail grid
// declares a fixed 260px track for this card, and the machine arrives on its own
// fetch — later than the report, and possibly never. The placeholder holds that
// track so the report body never renders crammed into 260px and never shifts
// when the card lands. See the R5 note in SrDetailV2.tsx.
//
// PHOTO COMES FROM THE SHARED 4-TIER FALLBACK (`useGalleryImages`), the same
// call ProfileHero and FleetCard make — files[] -> image -> catalog images ->
// catalog hero URL. Never re-implemented, never re-ordered.
//
// DELIBERATE ARTBOARD DEVIATION — NO CAPABILITY BADGES. The handoff overlays the
// M2.9 AI + telemetry badges on the photo. Rendering them honestly needs a LIVE
// gateway read (`CapabilityBadge` distinguishes "connected" from "can be
// connected"), and neither SR page fetches telemetry: MachineProfileV2's
// as-built rule is ONE telemetry mechanism, and standing up a second poller so
// a summary card can wear two decorative badges is both out of M2.6 scope and
// against `MASH-LIVE-IMPLEMENTATION.md`'s honesty rules — a badge derived from
// no telemetry would assert "not connected" about a machine that is. The
// readiness surface stays on the profile, one click away via the link below.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { useGalleryImages } from "../../../../shared/components/gallery";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  getBrandDisplayName,
  getMachineDisplayName,
  getMachineName,
} from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import { KV, MachinePhoto } from "../fleet/fleetCardAtoms";
import { SkeletonBar } from "../profile/tabs/tabAtoms";

/** The card's own chrome, shared with the skeleton so the two cannot drift. */
const cardChrome: React.CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  boxShadow: "var(--shadow)",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  minWidth: 0,
};

export interface SrMachineCardProps {
  machine: Machine;
  onOpenProfile: () => void;
}

const SrMachineCard: React.FC<SrMachineCardProps> = ({ machine, onOpenProfile }) => {
  const { translate } = useTranslation("myMachinery");

  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  const decommissioned = machine.isDecommissioned === true;
  const subtitle = getMachineName(machine);
  const location = machine.locationName
    ? (machine.parentLocationName ? machine.parentLocationName + " › " : "") + machine.locationName
    : translate("myMachinery:locationNotSet");

  return (
    <aside style={cardChrome}>
      <div style={{ position: "relative", alignSelf: "stretch" }}>
        <MachinePhoto
          src={images.length > 0 ? images[0].src : null}
          alt={getMachineDisplayName(machine)}
          grayscale={decommissioned}
          width="100%"
          height={140}
        />
        {images.length > 1 && (
          <span
            style={{
              position: "absolute",
              right: 8,
              bottom: 8,
              padding: "1px 7px",
              borderRadius: 999,
              background: "var(--card)",
              border: "1px solid var(--border)",
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              color: "var(--muted)",
            }}
          >
            {"1 / " + images.length}
          </span>
        )}
      </div>

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "var(--ink)",
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {getMachineDisplayName(machine)}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{subtitle}</div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <KV label={translate("myMachinery:sn")} value={machine.serialNumber} mono />
        <KV label={translate("myMachinery:brand")} value={getBrandDisplayName(machine)} />
        <KV label={translate("myMachinery:machineType")} value={machine.type?.name} />
        <KV label={translate("myMachinery:location")} value={location} />
      </div>

      <button
        type="button"
        onClick={onOpenProfile}
        style={{
          marginTop: 2,
          background: "transparent",
          border: 0,
          padding: 0,
          color: "var(--mod-accent)",
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 500,
          textAlign: "left",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {translate("myMachinery:srDetail.viewMachineProfile")} ›
      </button>
    </aside>
  );
};

/**
 * THE RESERVED TRACK (M2.6 review R5). Same chrome, same block rhythm, no data —
 * rendered in the card's grid cell while the machine fetch is still outstanding
 * (or after it failed), so the 260px column is never collapsed into the report
 * body and the real card drops in without moving anything.
 */
export const SrMachineCardSkeleton: React.FC = () => (
  <aside style={cardChrome} aria-hidden>
    <SkeletonBar height={140} radius={8} />
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <SkeletonBar height={15} width="72%" dim={1} />
      <SkeletonBar height={11} width="46%" dim={2} />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[0, 1, 2, 3].map((i) => (
        <SkeletonBar key={i} height={26} dim={i + 1} />
      ))}
    </div>
    <SkeletonBar height={13} width="55%" dim={4} />
  </aside>
);

export default SrMachineCard;
